import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import { pushAnalyticsEvent } from "@otta/analytics";
import { modularScale, palette } from "@otta/design-tokens";
import { Clickable } from "@otta/design";
import { Icon } from "@otta/icons";

/**
 * The intended use of this is
 * import * as Banner from "@hire/components/Banner"
 *
 * <Banner.Warning>
 *  <Banner.Content>
 *    Your banner text
 *  </Banner.Content>
 * <Banner.Warning>
 */

const BannerContainer = styled.div`
  background: ${palette.brand.yellow};
  border-radius: 1rem;
  display: flex;
  flex-direction: row;
  padding: 2.5rem 1.5rem;
  position: relative;
`;

const CornerIcon = styled(Clickable)`
  position: absolute;
  top: ${modularScale(1)};
  right: ${modularScale(1)};
`;

type BaseProps = React.PropsWithChildren<{
  name?: string;
  className?: string;
  dismissable: boolean;
}>;
type BannerProps = React.PropsWithChildren<{
  name?: string;
  className?: string;
  dismissable?: boolean;
}>;

/**
 * An alert that magically pushes an analytics event when viewed
 */
function BaseAlert({ name, children, dismissable }: BaseProps) {
  const { pathname } = useLocation();
  const localStorageKey = `dismissed-${name}`;
  const [dismissed, setDismissed] = useState(
    !!localStorage.getItem(localStorageKey)
  );

  useEffect(() => {
    if (!dismissed) {
      pushAnalyticsEvent({
        eventName: "Company Recruiter Viewed Alert",
        pathName: pathname,
        alertName: name,
      });
    }
  }, [pathname, name, dismissed]);

  const onClick = useCallback(() => {
    localStorage.setItem(localStorageKey, "true");
    setDismissed(true);
  }, [localStorageKey]);

  return dismissed ? null : (
    <BannerContainer>
      {children}
      {dismissable ? (
        <CornerIcon onClick={onClick} data-testid="close-icon">
          <Icon icon="close" size={1} />
        </CornerIcon>
      ) : null}
    </BannerContainer>
  );
}

export function Warning({
  name,
  children,
  className,
  dismissable = false,
}: BannerProps): React.ReactElement {
  return (
    <BaseAlert className={className} name={name} dismissable={!!dismissable}>
      {children}
    </BaseAlert>
  );
}

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;
